/**
 * Product Media Gallery Section Script (Modern Shopify Theme Architecture)
 *
 * Handles Flickity carousel (mobile), GLightbox (images/videos),
 * model-viewer loading, and video observation for the product media gallery.
 * This version uses Shopify's Section Rendering API for initialization.
 */

class ProductGallery {
  constructor(container) {
    this.container = container;
    this.sectionId = this.container.dataset.sectionId;
    if (!this.sectionId || !this.container) {
      console.error('Product Gallery: Missing container or sectionId.');
      return;
    }

    this.flickity = null;
    this.glightbox = null;
    this.modelViewerLoaded = false;
    this.videoObserver = null;

    this.init();
  }

  // --- Utility Functions for Loading Assets ---
  loadScript(src, callback) {
    if (document.querySelector(`script[src="${src}"]`)) {
      callback();
      return;
    }
    const script = document.createElement('script');
    script.src = src;
    script.onload = callback;
    script.onerror = () => console.error(`Failed to load script: ${src}`);
    document.head.appendChild(script);
  }

  loadStylesheet(href, callback) {
    if (document.querySelector(`link[href="${href}"]`)) {
      if (callback) callback();
      return;
    }
    const link = document.createElement('link');
    link.rel = 'stylesheet';
    link.href = href;
    link.onload = callback;
    link.onerror = () => console.error(`Failed to load stylesheet: ${href}`);
    document.head.appendChild(link);
  }

  // --- Core Initialization Logic ---
  init() {
    // An IntersectionObserver waits until the section is near the viewport to load assets.
    const observer = new IntersectionObserver((entries, obs) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          this.loadAssets();
          obs.unobserve(this.container); // Run only once
        }
      });
    }, { rootMargin: '50px' });

    observer.observe(this.container);

    // Event listeners for dynamic content (e.g., model viewer)
    this.container.addEventListener('click', (event) => {
      const modelButton = event.target.closest('.model-load-button');
      if (modelButton && modelButton.dataset.mediaId) {
        event.preventDefault();
        this.loadAndInitModelViewer(modelButton.dataset.mediaId);
      }
    });
  }

  loadAssets() {
    const mediaList = this.container.querySelector('.product-media-list');
    const mediaCount = parseInt(this.container.dataset.mediaCount, 10);

    // Load Flickity for mobile carousel
    if (window.innerWidth < 750 && mediaCount > 1) {
      this.initFlickity();
    }
    
    // Setup resize listener to manage Flickity instance
    let resizeTimeout;
    window.addEventListener('resize', () => {
      clearTimeout(resizeTimeout);
      resizeTimeout = setTimeout(() => {
        if (window.innerWidth < 750 && !this.flickity && mediaCount > 1) {
          this.initFlickity();
        } else if (window.innerWidth >= 750 && this.flickity) {
          this.destroyFlickity();
        }
      }, 250);
    });

    // Load GLightbox for popups
    if (this.container.querySelector('.glightbox-item')) {
      this.initGLightbox();
    }
    
    // Pre-load model-viewer script if needed
    if (this.container.querySelector('.model-load-button')) {
        this.loadModelViewerScript();
    }

    // Initialize observers for self-hosted videos
    if (this.container.querySelector('video.product-video')) {
        this.initVideoObserver();
    }

    // IMPORTANT: Make the section visible
    this.container.classList.add('is-initialized');
  }

  // --- Library Specific Initializers ---
  initFlickity() {
    const flickityJsUrl = this.container.dataset.flickityJsUrl;
    const flickityCssUrl = this.container.dataset.flickityCssUrl;
    
    this.loadStylesheet(flickityCssUrl, () => {
      this.loadScript(flickityJsUrl, () => {
        const mediaList = this.container.querySelector('.product-media-list');
        if (mediaList && !this.flickity) {
            this.flickity = new Flickity(mediaList, {
                cellAlign: 'center', contain: true, wrapAround: true, pageDots: true,
                prevNextButtons: false, cellSelector: '.product-media-list__item'
            });
        }
      });
    });
  }

  destroyFlickity() {
      if (this.flickity) {
          this.flickity.destroy();
          this.flickity = null;
      }
  }

  initGLightbox() {
    const gLightboxJsUrl = this.container.dataset.glightboxJsUrl;
    const gLightboxCssUrl = this.container.dataset.glightboxCssUrl;

    this.loadStylesheet(gLightboxCssUrl, () => {
        this.loadScript(gLightboxJsUrl, () => {
            if (!this.glightbox) {
                this.glightbox = GLightbox({
                    selector: `#${this.container.id} .glightbox-item`,
                    gallery: this.container.dataset.galleryId,
                });
            }
        });
    });
  }

  loadModelViewerScript(callback) {
    if (this.modelViewerLoaded) {
      if (callback) callback();
      return;
    }
    const modelViewerUrl = 'https://cdn.shopify.com/shopifycloud/model-viewer/v1.12/model-viewer.js';
    this.loadScript(modelViewerUrl, () => {
      this.modelViewerLoaded = true;
      if (callback) callback();
    });
  }

  loadAndInitModelViewer(mediaId) {
    const template = this.container.querySelector(`#ModelTemplate-${mediaId}`);
    const button = this.container.querySelector(`[id="productModelModalOpen-${this.sectionId}-${mediaId}"]`);
    const itemContainer = button?.closest('.product-media-item');
    if (!template || !button || !itemContainer) return;

    this.loadModelViewerScript(() => {
        if (itemContainer.querySelector('model-viewer')) return; // Already loaded
        const modelViewerElement = template.content.firstElementChild.cloneNode(true);
        itemContainer.querySelector('img')?.remove();
        button.remove();
        itemContainer.appendChild(modelViewerElement);
    });
  }

  initVideoObserver() {
    const videos = this.container.querySelectorAll('video.product-video');
    const shouldAutoplay = this.container.dataset.enableVideoAutoplay === 'true';

    this.videoObserver = new IntersectionObserver((entries) => {
        entries.forEach(entry => {
            const video = entry.target;
            if (entry.isIntersecting) {
                if (shouldAutoplay) video.play().catch(e => console.warn("Video autoplay failed:", e.message));
            } else {
                video.pause();
            }
        });
    }, { threshold: 0.5 });

    videos.forEach(video => this.videoObserver.observe(video));
  }

  // --- Shopify Theme Editor Hooks ---
  onUnload() {
    this.destroyFlickity();
    if (this.glightbox) this.glightbox.destroy();
    if (this.videoObserver) this.videoObserver.disconnect();
  }
}

// --- Load sections and listen for Shopify Theme Editor events ---
const sections = {};

function loadSection(container) {
  const sectionId = container.dataset.sectionId;
  if (sections[sectionId]) {
    sections[sectionId].onUnload(); // Clean up previous instance
  }
  sections[sectionId] = new ProductGallery(container);
}

document.addEventListener('shopify:section:load', (event) => {
  const container = document.getElementById(`product-gallery-section-${event.detail.sectionId}`);
  if (container) loadSection(container);
});

document.addEventListener('shopify:section:unload', (event) => {
    const sectionId = event.detail.sectionId;
    if (sections[sectionId]) {
        sections[sectionId].onUnload();
        delete sections[sectionId];
    }
});

// Initialize sections that are already on the page at load time
document.querySelectorAll('[id^="product-gallery-section-"]').forEach(container => {
    loadSection(container);
});